import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';


export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validHash, setValidHash] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if this is a valid recovery session
    const hash = window.location.hash;
    if (hash.includes('type=recovery') || hash.includes('access_token')) {
      setValidHash(true);
    } else {
      setError('Invalid or expired reset link. Please request a new one.');
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      toast.success('Password updated successfully!');
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="app-container flex min-h-screen flex-col items-center justify-center px-6 bg-background">
      <Helmet>
        <title>Reset Password · LoreScout</title>
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href="https://lorescout.com/reset-password" />
      </Helmet>
      <div className="w-full max-w-sm">
        <h1 className="font-display text-xl font-semibold text-foreground text-center mb-2">Reset Password</h1>
        <p className="text-sm text-muted-foreground font-body text-center mb-6">
          {validHash ? 'Enter your new password below.' : 'This link is invalid or expired.'}
        </p>

        {validHash ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="new-password" className="text-xs text-muted-foreground font-body">New Password</label>
              <div className="relative mt-1">
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2.5 pr-10 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="confirm-password" className="text-xs text-muted-foreground font-body">Confirm Password</label>
              <input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>

            {error && <p className="text-xs text-destructive font-body">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground font-body disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        ) : (
          <div className="text-center">
            <p className="text-xs text-destructive font-body mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-primary font-body"
            >
              Go back home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
